import { updateArray } from '../../updateArray';
import { ElectionState, EraseStatus, Translatable, VoteResult } from '../../_generatedTypes.g';
import { UnknownAction, Dispatch } from 'redux';
import * as api from '../../api';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { voterEntityStateRefreshed, innerStateSet, panicStateSet } from '../innerState/innerStateSlice';

export const vote = async (electionId: number, candidateId: number, token: string, dispatch: Dispatch<UnknownAction>) => {
    dispatch(voteResultChanged({electionId, voteResult: VoteResult.InProgress}));
    const body = new FormData();
    body.append('candidateId', String(candidateId));
    const _ = api.api(token);
    const voteResult = await _.Vote.Vote<VoteResult>(body)
        .anyStatus(voteResult => voteResult, api.ReadAs.Json);
    dispatch(voteResultChanged({electionId, voteResult}));
    await _.Elections.Get(electionId)
        .ok((entity: ElectionState) => dispatch(voteCountUpdated({electionId, voteCount: entity.VoteCount})), api.ReadAs.Json)
        .fail(() => dispatch(panicStateSet()));
};

export const blankVote = async (electionId: number, token: string, dispatch: Dispatch<UnknownAction>) => {
    dispatch(voteResultChanged({electionId, voteResult: VoteResult.InProgress}));
    const body = new FormData();
    body.append('electionId', String(electionId));
    const _ = api.api(token);
    const voteResult = await _.Vote.BlankVote<VoteResult>(body)
        .anyStatus(voteResult => voteResult, api.ReadAs.Json);
    dispatch(voteResultChanged({electionId, voteResult}));
    await _.Elections.Get(electionId)
        .ok((entity: ElectionState) => dispatch(voteCountUpdated({electionId, voteCount: entity.VoteCount})), api.ReadAs.Json)
        .fail(() => dispatch(panicStateSet()));
};

export const startEraseElection = async (election: ElectionState, token: string, dispatch: Dispatch<UnknownAction>) => {
    dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.Started}));
    await api.api(token).Elections.EraseElection<void>(election.ElectionId, {})
        .status(404, () => dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.FailedBecauseRemoved})))
        .status(405, () => dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.FailedBecauseOpen})))
        .status(200, electionList => {
            dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.Succeeded}));
            setTimeout(() => {
                dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.Idle}));
                dispatch(electionsRefreshed(electionList));
            }, 3000);
        }, api.ReadAs.Json)
        .anyStatus(() => dispatch(electionErased({electionId: election.ElectionId, status: EraseStatus.FailedForUnknownReason})))
        .fail(() => dispatch(panicStateSet()));
}

const electionsSlice = createSlice({
    name: 'SLICE_elections',
    initialState: {} as ElectionState[],
    reducers: {
        voteResultChanged(state, action: PayloadAction<{ electionId: number, voteResult: VoteResult }>) {
            return updateArray(state, {
                with: e => ({
                    ...e,
                    VoteResult: action.payload.voteResult,
                }),
                where: e => e.ElectionId === action.payload.electionId,
            });
        },
        voteCountUpdated(state, action: PayloadAction<{ electionId: number, voteCount: number }>) {
            return updateArray(state, {
                with: e => ({
                    ...e,
                    VoteCount: action.payload.voteCount,
                }),
                where: e => e.ElectionId === action.payload.electionId,
            });
        },
        electionErased(state, action: PayloadAction<{ electionId: number, status: EraseStatus }>) {
            return updateArray(state, {
                with: e => ({
                    ...e,
                    EraseStatus: action.payload.status,
                }),
                where: e => e.ElectionId === action.payload.electionId,
            });
        },
        electionReplaced(state, action: PayloadAction<ElectionState>) {
            return updateArray(state, {
                with: e => action.payload,
                where: e => e.Code === action.payload.Code,
            });
        },
        electionsRefreshed(state, action: PayloadAction<ElectionState[]>) {
            return action.payload;
        },
        },
        extraReducers: (builder) => {
            builder.addCase(innerStateSet, (state, action) => {
                if (action.payload.Type === 'LOGGED_IN_STATE') {
                    return action.payload.VoterEntities.Elections;
                } else {
                    return [];
                }
            });
            builder.addCase(voterEntityStateRefreshed, (state, action) => action.payload.Elections);
        },
    });

export const {
    voteResultChanged,
    voteCountUpdated,
    electionErased,
    electionReplaced,
    electionsRefreshed,
} = electionsSlice.actions

export default electionsSlice.reducer


export function suggestName(originalName: Translatable, allNames: string[]) {

    if (originalName.EN.substring(0, Math.min(originalName.EN.length, 7)) !== 'Copy of') {
        return { EN: `Copy of ${originalName.EN}`, NL: `Kopie van ${originalName.NL}` };
    }

    return {
        NL: suggestUniqueName(originalName.NL, allNames),
        EN: suggestUniqueName(originalName.EN, allNames)
    }
}

function suggestUniqueName(originalName: string, allNames: string[]) {
    let suggestion = ''
    {
        var i = 1;
        while (true) {
            let match;
            if (match = /-([0-9a-f]+)$/.exec(originalName)) {
                const suffix = parseInt(match[1], 16) + i;
                suggestion = originalName.substring(0, match.index) + '-' + suffix.toString(16);
            } else {
                suggestion = originalName + '-' + (1 + i);
            }
            if (!allNames.find(x => x === suggestion)) {
                return suggestion;
            }
            i++;
        }
    }
}

